import { useEffect, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Box, TextField, Alert } from '@mui/material';

const PlaceAutocompleteElementWrapper = ({ 
  onPlaceSelect, 
  label, 
  value = '', 
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = true,
  placeholder = '',
  // State wird von übergeordneter Komponente verwaltet
  hasPrefilledValue = false,
  isEditingMode = false,
  onEditingModeActivation,
  ...textFieldProps 
}) => {
  const [placeAutocompleteElement, setPlaceAutocompleteElement] = useState(null);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [apiError, setApiError] = useState(null);
  const containerRef = useRef(null);
  const places = useMapsLibrary('places');

  // State wird jetzt von übergeordneter Komponente verwaltet - entferne lokalen useEffect

  useEffect(() => {
    if (!places || !containerRef.current || disabled || placeAutocompleteElement || hasPrefilledValue) return;

    try {
      // Prüfe ob PlaceAutocompleteElement verfügbar ist (beta/alpha channel)
      if (!places.PlaceAutocompleteElement) {
        console.warn('PlaceAutocompleteElement nicht verfügbar, verwende Fallback-Modus');
        setFallbackMode(true);
        return;
      }

      // Stelle sicher, dass der Container leer ist
      if (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }

      const options = {
        requestedLanguage: 'de',
        requestedRegion: 'DE',
      };

      const autocompleteElement = new places.PlaceAutocompleteElement(options);
      
      
      // Minimales Styling - lass Google Maps das meiste selbst handhaben
      autocompleteElement.style.width = '100%';
      autocompleteElement.style.display = 'block';
      
      if (placeholder) {
        autocompleteElement.placeholder = placeholder;
      }
      
      // Setze den aktuellen Wert wenn vorhanden
      if (value) {
        autocompleteElement.value = value;
      }

      // Event-Listener werden in separatem useEffect hinzugefügt

      // Element zum Container hinzufügen
      containerRef.current.appendChild(autocompleteElement);
      setPlaceAutocompleteElement(autocompleteElement);
      setApiError(null);

    } catch (error) {
      console.error('Fehler beim Initialisieren von PlaceAutocompleteElement:', error);
      setFallbackMode(true);
      setApiError('Places API nicht verfügbar');
    }

    return () => {
      if (placeAutocompleteElement && containerRef.current && containerRef.current.contains(placeAutocompleteElement)) {
        try {
          containerRef.current.removeChild(placeAutocompleteElement);
        } catch (error) {
          // Element bereits entfernt
        }
      }
      setPlaceAutocompleteElement(null);
    };
  }, [places, disabled, hasPrefilledValue]);

  // Synchronisiere den Wert mit dem Element
  useEffect(() => {
    if (placeAutocompleteElement && value !== undefined) {
      // Nur setzen wenn der Wert sich unterscheidet, um Endlos-Schleifen zu vermeiden
      if (placeAutocompleteElement.value !== value) {
        placeAutocompleteElement.value = value;
      }
    }
  }, [value, placeAutocompleteElement]);

  // Separate useEffect für Event-Listener Updates
  useEffect(() => {
    if (!placeAutocompleteElement) return;

    const handleGmpSelect = async (event) => {
      try {
        const place = event.placePrediction.toPlace();
        
        await place.fetchFields({ 
          fields: ['displayName', 'formattedAddress', 'location'] 
        });

        const formattedAddress = place.formattedAddress || place.displayName || '';
        const location = {
          latitude: place.location.lat(),
          longitude: place.location.lng()
        };


        if (onPlaceSelect) {
          onPlaceSelect(formattedAddress, location);
        }
      } catch (error) {
        console.error('Fehler beim Verarbeiten der Ortsauswahl:', error);
        setApiError('Fehler beim Laden der Ortsdaten');
      }
    };

    const handleInput = (event) => {
      // Bei PlaceAutocompleteElement muss der Wert direkt vom Element gelesen werden
      const currentValue = placeAutocompleteElement.value || '';
      
      if (onChange) {
        onChange({ target: { value: currentValue } });
      }
    };

    // Entferne alte Listener falls vorhanden
    placeAutocompleteElement.removeEventListener('input', handleInput);
    
    // Füge neue Listener hinzu
    placeAutocompleteElement.addEventListener('input', handleInput);
    
    // Event-Listener für Place Selection
    placeAutocompleteElement.addEventListener('gmp-select', handleGmpSelect);

    return () => {
      if (placeAutocompleteElement) {
        placeAutocompleteElement.removeEventListener('gmp-select', handleGmpSelect);
        placeAutocompleteElement.removeEventListener('input', handleInput);
      }
    };
  }, [placeAutocompleteElement, onPlaceSelect, onChange]);

  // Handler wird von übergeordneter Komponente bereitgestellt
  const handleEditingModeActivation = onEditingModeActivation || (() => {});

  // Wenn wir eine vorausgefüllte Adresse haben und noch nicht im Bearbeitungsmodus sind,
  // zeige normales TextField
  if (hasPrefilledValue && !isEditingMode) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={onChange}
        onFocus={handleEditingModeActivation}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        placeholder={placeholder || 'Adresse eingeben...'}
        InputProps={{
          style: { cursor: 'pointer' }
        }}
        {...textFieldProps}
      />
    );
  }

  // Fallback auf normales TextField wenn PlaceAutocompleteElement nicht verfügbar
  if (fallbackMode || !places) {
    return (
      <Box>
        {apiError && (
          <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
            {apiError} - Verwende einfaches Textfeld
          </Alert>
        )}
        <TextField
          label={label}
          value={value}
          onChange={onChange}
          error={error}
          helperText={helperText}
          required={required}
          disabled={disabled}
          fullWidth={fullWidth}
          placeholder={placeholder || 'Adresse eingeben...'}
          {...textFieldProps}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Label */}
      {label && (
        <Box sx={{ mb: 1 }}>
          <label style={{ fontSize: '0.875rem', color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)' }}>
            {label}
            {required && ' *'}
          </label>
        </Box>
      )}
      
      {/* Container für PlaceAutocompleteElement */}
      <Box
        ref={containerRef}
        sx={{
          '& gmp-place-autocomplete': {
            width: '100%',
            display: 'block',
            // Alle möglichen Input-Selektoren abdecken
            '& input, & input[type="text"], & input[type="search"], & .pac-input': {
              border: error ? '2px solid #d32f2f !important' : '1px solid rgba(0, 0, 0, 0.23) !important',
              borderRadius: '4px !important',
              padding: '14px !important',
              fontSize: '16px !important',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
              '&:focus': {
                borderColor: error ? '#d32f2f !important' : '#1976d2 !important',
              },
              '&:hover': {
                borderColor: error ? '#d32f2f !important' : 'rgba(0, 0, 0, 0.87) !important',
              }
            }
          }
        }}
      />
      
      
      {/* Helper Text */}
      {(helperText || error) && (
        <Box sx={{ mt: 0.5, ml: 1.75 }}>
          <span 
            style={{ 
              fontSize: '0.75rem', 
              color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)' 
            }}
          >
            {helperText}
          </span>
        </Box>
      )}
    </Box>
  );
};

export default PlaceAutocompleteElementWrapper;